import { EntityForm, Notice, Text } from '@lbcamden/lib-react';
import { FunctionComponent } from 'react';

import { useUserStore } from '~/client/hooks/storeHooks';
import { PcfraPerson, PcfraStatus } from '~/common/types/people';

import {
  notifyReferrerMessage,
  commonFormConfig,
  commonFormSchema,
  CommonFormValues,
} from '../config';
import { CurrentResidentsDialog, DialogContent } from '../CurrentResidents.styled';
import { getPersonTitleAndSurname } from '../helpers';
import { useHandlePcfraActivity } from '../useHandlePcfraActivity';

type Props = {
  open?: boolean;
  person?: PcfraPerson;
  draftId?: string;
  onClose(): void;
};

export const UnnecessaryStatusDialog: FunctionComponent<Props> = ({
  open,
  person,
  draftId,
  onClose,
}) => {
  const userStore = useUserStore();
  const handlePcfraActivity = useHandlePcfraActivity();

  const hasDraft = person?.pcfraStatus === PcfraStatus.Drafting && draftId;

  const title = hasDraft
    ? 'Discard this draft?'
    : `Remove ${getPersonTitleAndSurname(person)} from the queue?`;

  const onSave = ({ note, pcfraReviewDate }: CommonFormValues) => {
    if (!person) {
      return;
    }
    const { propertyId, personId } = person;

    userStore?.store.transaction(() => {
      if (draftId) {
        userStore.handleAndDispatchMessage('deletePcfraDraft', draftId, { propertyId, draftId });
      }
      handlePcfraActivity({
        propertyId,
        personId,
        note,
        newPcfraStatus: PcfraStatus.Unnecessary,
        pcfraReviewDate: pcfraReviewDate || null,
        shouldNotifyReferrer: true,
      });
    });

    onClose();
  };

  return (
    <CurrentResidentsDialog open={open} title={title} accessibleInfo={{ title }} onClose={onClose}>
      <DialogContent>
        {hasDraft && (
          <Notice
            type="warning"
            title="Warning"
            message="All entered data will be deleted and a PCFRA will not be created."
          />
        )}
        <Text>
          By taking this action, you are confirming that the resident no longer requires a PCFRA.
        </Text>
        <EntityForm<CommonFormValues>
          inDialog
          schema={commonFormSchema}
          config={commonFormConfig}
          initialValue={{ pcfraReviewDate: person?.pcfraReviewDate }}
          message={notifyReferrerMessage}
          onCancel={onClose}
          onSave={onSave}
        />
      </DialogContent>
    </CurrentResidentsDialog>
  );
};
